import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import WFSLayer from "@arcgis/core/layers/WFSLayer";
import * as projection from "@arcgis/core/geometry/projection";
import { PREFERRED_WKID } from "../../CustomAreas/UserDataContextContainer";
import Extent from "@arcgis/core/geometry/Extent";
import squareGrid from "@turf/square-grid";
import Polygon from "@arcgis/core/geometry/Polygon";
import useUseDebug from '../../../App/useUseDebug';
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import iteratedForestMaskRenderer from "../components/Map/IteratedForestMaskRenderer";
import { DECLARATIONS_MIN_SCALE_TO_SHOW } from "../../../App/useNotificationsLayer";
import intersectingForestMaskLayerPopup from "../components/Map/intersectingForestMaskLayerPopup";
import { useContext, useEffect, useMemo } from "react";
import { MapContext } from "../../../App/MapContextContainer";

export function useAdditionalDebugging(webmap: WebMap, view: MapView, urbanAreasLayer: GeoJSONLayer, forestMaskLayer: WFSLayer) {
    const preferredSpatialReference = {
        wkid: PREFERRED_WKID
    };

    const mapContext = useContext(MapContext);

    const squareSizeKilometers = 5;

    const { isDebugOn, debugRectangles, debugSquare } = useUseDebug(webmap, view);

    const intersectingForestMaskLayer = useMemo(() => isDebugOn && new FeatureLayer({
        source: [],
        renderer: iteratedForestMaskRenderer,
        spatialReference: { wkid: 4326 },
        minScale: DECLARATIONS_MIN_SCALE_TO_SHOW,
        objectIdField: "__esri_wfs_id__",
        popupEnabled: true,
        popupTemplate: intersectingForestMaskLayerPopup,
        fields: [{
            name: "__esri_wfs_id__",
            type: "oid"
        }, {
            name: "area",
            type: "double"
        },
        {
            name: "iteration",
            type: "double"
        }],
        title: "Lähimetsät (debug)",
        geometryType: "polygon"
    }), [isDebugOn]);

    const drawBBoxOfAreaWeWantToSave = async (geometryInPreferredWkid: Extent) => {
        if (isDebugOn) {
            const bbox = [geometryInPreferredWkid.extent.xmin, geometryInPreferredWkid.extent.ymin, geometryInPreferredWkid.extent.xmax, geometryInPreferredWkid.extent.ymax];

            const grid = squareGrid(bbox as any, squareSizeKilometers, { units: "kilometers" });

            for (const square of grid.features) {
                const squarePolygon = new Polygon({
                    rings: square.geometry.coordinates
                });

                const projected = projection.project(squarePolygon, view.spatialReference) as Polygon;

                debugSquare(projected);
            }
        }
    }

    const handleBufferedUrbanAreasUpdate = async () => {
        if (intersectingForestMaskLayer === false) {
            return;
        }

        const queryArea = urbanAreasLayer.createQuery();
        const urbanAreas = await urbanAreasLayer.queryFeatures(queryArea);

        await putAllForestMaskInAnotherLayer(forestMaskLayer, intersectingForestMaskLayer);

        let geometryInPreferredWkid = projection.project(view.extent, preferredSpatialReference) as Extent;
        const bbox = [geometryInPreferredWkid.extent.xmin, geometryInPreferredWkid.extent.ymin, geometryInPreferredWkid.extent.xmax, geometryInPreferredWkid.extent.ymax];

        const grid = squareGrid(bbox as any, 8, { units: "kilometers" });

        for (const square of grid.features) {
            const squarePolygon = new Polygon({
                rings: square.geometry.coordinates
            });

            const projected = projection.project(squarePolygon, view.spatialReference) as Polygon;

            debugSquare(projected);
        }

        console.log(urbanAreas.features.length + " urban areas");

        for (const urbanArea of urbanAreas.features) {
            const queryForestMask = intersectingForestMaskLayer.createQuery();

            queryForestMask.geometry = urbanArea.geometry;

            queryForestMask.spatialRelationship = "overlaps"
            queryForestMask.where = "iteration = 0";
            queryForestMask.returnGeometry = true;

            const intersectingForestMask = await intersectingForestMaskLayer.queryFeatures(queryForestMask);

            await addForestsToMap(intersectingForestMask.features, 1, intersectingForestMaskLayer);
        }
    };

    const addForestsToMap = async (features: Graphic[], iteration: number, intersectingForestMaskLayer: FeatureLayer) => {
        let bufferedGraphics: Graphic[] = [];

        console.log("Start looping forest mask - iteration " + iteration + " with " + features.length + " features");

        const featuresWithoutIteration = features.filter(f => f.attributes.iteration === 0);

        for (const feature of featuresWithoutIteration) {
            // const queryNotifications = notificationsLayer.createQuery();
            // queryNotifications.geometry = feature.geometry;
            // queryNotifications.spatialRelationship = "intersects";
            // queryNotifications.returnGeometry = true;

            // const containedNotifications = await notificationsLayer.queryFeatures(queryNotifications);

            // let geometryToUse: Geometry;

            // if (containedNotifications.features.length > 0) {
            //     try {
            //         const intersectingPart = await intersect(feature.geometry, containedNotifications.features[0].geometry);
            //         const geometries = await symmetricDifference(feature.geometry, intersectingPart);
            //         geometryToUse = Array.isArray(geometries) ? geometries[0] : geometries;
            //     } catch (error) {
            //         console.log("Some error getting intersection and difference");
            //         geometryToUse = feature.geometry;
            //     }
            // }
            // else {
            //     geometryToUse = feature.geometry;
            // }

            feature.attributes.iteration = iteration;

            // bufferedGraphics.push(new Graphic({
            //     geometry: feature.geometry,
            //     attributes: {
            //         area: feature.attributes.area,
            //         iteration
            //     }
            // }));
        }

        const addEdits = {
            //addFeatures: bufferedGraphics
            updateFeatures: featuresWithoutIteration
        };

        const result = await intersectingForestMaskLayer.applyEdits(addEdits);

        //console.log(result);

        for (const feature of features) {
            const queryNearForests = intersectingForestMaskLayer.createQuery();
            queryNearForests.returnGeometry = true;
            queryNearForests.geometry = feature.geometry;
            queryNearForests.spatialRelationship = 'touches';
            //queryNearForests.distance = 6;
            //queryNearForests.units = "meters";
            queryNearForests.where = "iteration = 0";

            const currentForestsNearUs = await intersectingForestMaskLayer.queryFeatures(queryNearForests);

            await addForestsToMap(currentForestsNearUs.features, iteration++, intersectingForestMaskLayer);
        }
    };

    const putAllForestMaskInAnotherLayer = async (forestMaskLayer: WFSLayer, intersectingForestMaskLayer: FeatureLayer) => {
        const queryForestMask = forestMaskLayer.createQuery();
        queryForestMask.returnGeometry = true;

        const wholeForestMask = await forestMaskLayer.queryFeatures(queryForestMask);

        const bufferedGraphics: Graphic[] = [];

        wholeForestMask.features.forEach(feature => {
            const f = feature.clone();
            f.attributes.iteration = 0;
            bufferedGraphics.push(f);
        });

        const addEdits = {
            addFeatures: bufferedGraphics
        };

        const res = await intersectingForestMaskLayer.applyEdits(addEdits);

        console.log(res);
    }

    useEffect(() => {
        if (intersectingForestMaskLayer) {
            webmap.add(intersectingForestMaskLayer);

            view.whenLayerView(urbanAreasLayer).then((urbanAreasLayerView) => {
                urbanAreasLayer.on("refresh", async function (event) {
                    if (event.dataChanged && isDebugOn && false) {
                        await handleBufferedUrbanAreasUpdate();
                    }
                });
            });
        }
    }, [mapContext.mapAndViewReady, isDebugOn]);

    return {
        putAllForestMaskInAnotherLayer,
        handleBufferedUrbanAreasUpdate,
        isDebugOn,
        debugSquare,
        drawBBoxOfAreaWeWantToSave,
        debugRectangles
    }
}
